<template>
  <div class="container-fluid py-3">
    <!-- Header -->
    <div class="d-flex align-items-center mb-3">
      <button
        class="btn btn-sm btn-outline-secondary mr-2"
        @click="$router.push({ name: 'regulacao' })"
      >
        <feather-icon icon="ArrowLeftIcon" size="14" />
      </button>
      <h4 class="mb-0 flex-grow-1">
        <feather-icon icon="ActivityIcon" size="20" class="mr-50" />
        Regulação
        <span v-if="occurrence" class="text-muted font-weight-normal ml-1">
          — {{ occurrence.protocol || "#" + occurrence.id }}
        </span>
      </h4>
    </div>

    <!-- Loading -->
    <div v-if="loadingOccurrence" class="text-center py-5">
      <b-spinner variant="primary" />
      <p class="mt-2 text-muted">Carregando...</p>
    </div>

    <!-- Not found -->
    <div v-else-if="!occurrence" class="text-center py-5 text-muted">
      <feather-icon icon="AlertCircleIcon" size="40" class="mb-2" />
      <p>Ocorrência não encontrada.</p>
    </div>

    <div v-else>
      <!-- ── Bloco fixo: Informações da Ocorrência ─────────────────── -->
      <occurrence-info-block
        :occurrence="occurrence"
        :involveds-count="involvedItems.length"
      />

      <!-- ── Pipeline horizontal ──────────────────────────────────── -->
      <div class="occ-htl mb-3">
        <div class="occ-htl-track">
          <template v-for="(step, idx) in pipelineSteps">
            <div
              v-if="idx > 0"
              :key="'line-' + idx"
              class="occ-htl-connector"
              :class="{
                'occ-htl-connector--done': pipelineSteps[idx - 1].state === 'done' && step.state === 'done',
              }"
              :style="pipelineSteps[idx - 1].state === 'done' && step.state === 'active'
                ? { background: step.variant === 'amber' ? '#F59E0B' : step.variant === 'blue' ? '#2563EB' : '#7367f0' }
                : {}"
            />
            <div :key="step.key" class="occ-htl-node">
              <div
                class="occ-htl-dot"
                :class="
                  step.state === 'done'
                    ? 'occ-htl-dot--done'
                    : step.state === 'active'
                    ? 'occ-htl-dot--active-' + (step.variant || 'primary')
                    : 'occ-htl-dot--pending'
                "
              >
                <feather-icon
                  v-if="step.state === 'done'"
                  icon="CheckIcon"
                  size="10"
                  style="color: #fff"
                />
                <feather-icon
                  v-else
                  :icon="step.icon"
                  size="10"
                  :style="{
                    color: step.state === 'active' ? '#fff' : '#b9b9c3',
                  }"
                />
              </div>
              <span
                class="occ-htl-label"
                :class="
                  step.state === 'done'
                    ? 'occ-htl-label--done'
                    : step.state === 'active'
                    ? 'occ-htl-label--active-' + (step.variant || 'primary')
                    : ''
                "
                >{{ step.label }}</span
              >
            </div>
          </template>
        </div>
      </div>
      <!-- /Pipeline horizontal -->

      <!-- ── Layout: Sidebar + Conteúdo ───────────────────────────── -->
      <div class="card tab-layout-card mb-3">
        <div class="d-flex">
          <!-- Sidebar de navegação -->
          <div class="tab-sidebar">
            <button
              v-for="tab in tabs"
              :key="tab.key"
              class="tab-sidebar-btn"
              :class="{ active: activeTab === tab.key }"
              @click="activeTab = tab.key"
              :title="tab.label"
            >
              <feather-icon :icon="tab.icon" size="20" />
              <span class="tab-sidebar-label">{{ tab.label }}</span>
            </button>
          </div>

          <!-- Conteúdo da aba ativa -->
          <div class="tab-content-area">
            <!-- ══ ABA: Resumo ════════════════════════════════════════ -->
            <div v-if="activeTab === 'resumo'">
              <!-- Strip: abertura / autor / etapa -->
              <!-- <div class="resumo-strip mb-3">
                <div class="resumo-strip-card">
                  <span class="b-avatar badge-light-primary rounded">
                    <feather-icon icon="ClockIcon" size="16" />
                  </span>
                  <div class="ml-75">
                    <small class="resumo-strip-label">Aberta em</small>
                    <strong class="resumo-strip-value">{{ occurrence.created_at | formatDate }}</strong>
                  </div>
                </div>
                <div class="resumo-strip-divider" />
                <div class="resumo-strip-card">
                  <span class="b-avatar badge-light-info rounded">
                    <feather-icon icon="UserCheckIcon" size="16" />
                  </span>
                  <div class="ml-75">
                    <small class="resumo-strip-label">Aberta por</small>
                    <strong class="resumo-strip-value">{{ occurrence.created_by ? occurrence.created_by.name : '—' }}</strong>
                  </div>
                </div>
                <div class="resumo-strip-divider" />
                <div class="resumo-strip-card">
                  <span class="b-avatar badge-light-warning rounded">
                    <feather-icon icon="LayersIcon" size="16" />
                  </span>
                  <div class="ml-75">
                    <small class="resumo-strip-label">Etapa atual</small>
                    <strong class="resumo-strip-value">{{ stepLabelText(occurrence.current_step) }}</strong>
                  </div>
                </div>
              </div> -->

              <!-- Queixa -->
              <div class="resumo-queixa mb-3">
                <div class="resumo-queixa__head">
                  <feather-icon
                    icon="MessageSquareIcon"
                    size="12"
                    class="mr-25"
                  />
                  Queixa reportada
                </div>
                <p class="resumo-queixa__body mb-0">
                  {{ occurrence.complaint || "Queixa não informada." }}
                </p>
              </div>

              <!-- Tabela de detalhes -->
              <div class="card mb-3">
                <div class="card-body p-0">
                  <table class="resumo-dt w-100">
                    <tbody>
                      <tr>
                        <td class="resumo-dt__label">Disciplina</td>
                        <td class="resumo-dt__value">
                          <span
                            v-if="occurrence.discipline"
                            class="occ-tag occ-tag--discipline"
                            >{{ occurrence.discipline.name }}</span
                          >
                          <span v-else class="text-muted">—</span>
                        </td>
                      </tr>
                      <tr>
                        <td class="resumo-dt__label">Tipo</td>
                        <td class="resumo-dt__value">
                          <span
                            v-if="occurrence.dispatch_type"
                            class="occ-tag occ-tag--type"
                            >{{ occurrence.dispatch_type.name }}</span
                          >
                          <span v-else class="text-muted">—</span>
                        </td>
                      </tr>
                      <tr>
                        <td class="resumo-dt__label">Motivo</td>
                        <td class="resumo-dt__value">
                          <span
                            v-if="occurrence.dispatch_reason"
                            class="occ-tag occ-tag--reason"
                            >{{ occurrence.dispatch_reason.name }}</span
                          >
                          <span v-else class="text-muted">—</span>
                        </td>
                      </tr>
                      <tr>
                        <td class="resumo-dt__label">Classificação</td>
                        <td class="resumo-dt__value">
                          <div
                            v-if="occurrence.classification"
                            class="occ-classification-badge"
                            style="margin-top: 0; display: inline-flex"
                            :style="{
                              backgroundColor: classificationColor + '18',
                              borderColor: classificationColor,
                              color: classificationColor,
                            }"
                          >
                            <feather-icon
                              icon="AlertCircleIcon"
                              size="11"
                              class="mr-25"
                            />
                            {{ occurrence.classification.color_label }}
                          </div>
                          <span v-else class="text-muted"
                            >Não classificada</span
                          >
                        </td>
                      </tr>
                      <tr>
                        <td class="resumo-dt__label">Interrogatório</td>
                        <td class="resumo-dt__value">
                          <span
                            v-if="occurrenceInterrogatories.length > 0"
                            class="resumo-badge resumo-badge--success"
                          >
                            <feather-icon
                              icon="CheckCircleIcon"
                              size="11"
                              class="mr-25"
                            />
                            {{
                              occurrenceInterrogatories.length === 1
                                ? "Respondido"
                                : occurrenceInterrogatories.length +
                                  " respondidos"
                            }}
                          </span>
                          <span
                            v-else-if="pendingInterrogatory"
                            class="resumo-badge resumo-badge--warning"
                          >
                            <feather-icon
                              icon="AlertCircleIcon"
                              size="11"
                              class="mr-25"
                            />
                            Pendente
                          </span>
                          <span v-else class="text-muted"
                            >Nenhum vinculado</span
                          >
                        </td>
                      </tr>
                      <tr>
                        <td class="resumo-dt__label">Equipes sugeridas</td>
                        <td class="resumo-dt__value">
                          <template
                            v-if="
                              occurrence.team_types &&
                              occurrence.team_types.length
                            "
                          >
                            <span
                              v-for="tt in occurrence.team_types"
                              :key="tt.id"
                              class="occ-team-badge mr-25"
                              >{{ tt.name }}</span
                            >
                          </template>
                          <span v-else class="text-muted"
                            >Nenhuma definida</span
                          >
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>


              <!-- Linha do tempo + Mensagens -->
              <div class="resumo-tl-section">
                <div class="row">
                  <div class="col-lg-6 col-12">
                    <div class="resumo-tl-title mb-3">
                      <feather-icon
                        icon="GitMergeIcon"
                        size="14"
                        class="mr-50 text-primary"
                      />
                      Linha do Tempo das Etapas
                    </div>
                    <occurrence-timeline :occurrence-id="occurrence.id" />
                  </div>

                  <div class="col-lg-6 col-12">
                    <div class="resumo-tl-title mb-3">
                      <feather-icon
                        icon="MessageSquareIcon"
                        size="14"
                        class="mr-50 text-primary"
                      />
                      Mensagens
                    </div>
                    <occurrence-messages
                      :occurrence-id="occurrence.id"
                      origin="regulacao"
                    />
                  </div>
                </div>
              </div>
            </div>
            <!-- /ABA: Resumo -->

            <!-- ══ ABA: Envolvidos ════════════════════════════════════ -->
            <div v-else-if="activeTab === 'envolvidos'">
              <div
                class="d-flex align-items-center justify-content-between mb-3"
              >
                <h5 class="mb-0">
                  <feather-icon
                    icon="UsersIcon"
                    size="18"
                    class="mr-50 text-primary"
                  />
                  Envolvidos
                </h5>
                <button
                  class="btn btn-sm btn-primary"
                  @click="showAddInvolvedModal = true"
                >
                  <feather-icon icon="PlusIcon" size="14" class="mr-25" />
                  Adicionar Envolvido
                </button>
              </div>

              <div v-if="loadingInvolved" class="text-center py-5">
                <b-spinner variant="primary" />
              </div>

              <div v-else-if="involvedItems.length === 0" class="card">
                <div class="card-body text-center text-muted py-5">
                  <feather-icon icon="UserXIcon" size="36" class="mb-2" />
                  <p class="mb-0">Nenhum envolvido cadastrado.</p>
                </div>
              </div>

              <div v-else>
                <involved-clinical-card
                  v-for="item in involvedItems"
                  :id="`involved-card-${item.id}`"
                  :key="item.id"
                  :involved="item"
                  :occurrence-id="occurrence.id"
                  :discipline-id="occurrence.discipline_id"
                  :occurrence-latitude="occurrence.latitude"
                  :occurrence-longitude="occurrence.longitude"
                  :occurrence-dispatch-type-id="occurrence.dispatch_type_id"
                  :occurrence-dispatch-reason-id="occurrence.dispatch_reason_id"
                  :open-panel-key="focusInvolvedId === item.id ? focusPanelKey : null"
                  class="mb-2"
                  @outcome-saved="onOutcomeSaved"
                  @edited="loadInvolved"
                />
              </div>
            </div>
            <!-- /ABA: Envolvidos -->

            <!-- ══ ABA: Interrogatórios ═══════════════════════════════ -->
            <div v-else-if="activeTab === 'interrogatorios'">
              <div
                class="d-flex align-items-center justify-content-between mb-3"
              >
                <h5 class="mb-0">
                  <feather-icon
                    icon="ClipboardIcon"
                    size="18"
                    class="mr-50 text-info"
                  />
                  Interrogatórios
                </h5>
                <button
                  v-if="pendingInterrogatory"
                  class="btn btn-sm btn-primary"
                  @click="openInterrFlow"
                >
                  <feather-icon icon="PlayIcon" size="14" class="mr-25" />
                  Responder Interrogatório
                </button>
              </div>

              <div v-if="loadingInterrogatories" class="text-center py-5">
                <b-spinner variant="primary" />
              </div>

              <div
                v-else-if="
                  occurrenceInterrogatories.length === 0 &&
                  !pendingInterrogatory
                "
                class="interr-empty"
              >
                <feather-icon icon="ClipboardIcon" size="32" class="mb-1" />
                <p class="mb-0">
                  Nenhum interrogatório vinculado a esta ocorrência.
                </p>
              </div>

              <div v-else class="interr-list">
                <!-- Pendente -->
                <div
                  v-if="pendingInterrogatory"
                  class="interr-item interr-item--pending"
                >
                  <div class="interr-item__icon">
                    <feather-icon icon="AlertCircleIcon" size="20" />
                  </div>
                  <div class="interr-item__body">
                    <span class="interr-item__name">{{
                      pendingInterrogatory.name
                    }}</span>
                    <span class="interr-item__badge interr-item__badge--warning"
                      >Pendente</span
                    >
                  </div>
                  <button
                    class="btn btn-sm btn-warning ml-auto"
                    @click="openInterrFlow"
                  >
                    <feather-icon icon="PlayIcon" size="13" class="mr-25" />
                    Responder
                  </button>
                </div>

                <!-- Respondidos -->
                <div
                  v-for="(oi, idx) in occurrenceInterrogatories"
                  :key="oi.id"
                  class="interr-item interr-item--done"
                >
                  <div class="interr-item__icon">
                    <feather-icon icon="CheckCircleIcon" size="20" />
                  </div>
                  <div class="interr-item__body">
                    <span class="interr-item__name">
                      {{
                        oi.interrogatory
                          ? oi.interrogatory.name
                          : "Interrogatório #" + (idx + 1)
                      }}
                    </span>
                    <span class="interr-item__badge interr-item__badge--success"
                      >Respondido</span
                    >
                    <small v-if="oi.created_at" class="interr-item__date">
                      {{ oi.created_at | formatDate }}
                    </small>
                  </div>
                  <button
                    class="btn btn-sm btn-outline-secondary ml-auto"
                    @click="reviewInterrogatory(oi)"
                  >
                    <feather-icon icon="EyeIcon" size="13" class="mr-25" />
                    Ver respostas
                  </button>
                </div>
              </div>
            </div>
            <!-- /ABA: Interrogatórios -->

            <!-- ══ ABA: Decisões Técnicas ═══════════════════════════════ -->
            <div v-else-if="activeTab === 'decisoes'">
              <h5 class="mb-3">
                <feather-icon
                  icon="ZapIcon"
                  size="18"
                  class="mr-50 text-warning"
                />
                Decisões Técnicas
              </h5>
              <technical-decision-panel
                ref="decisionPanel"
                :occurrence-id="occurrence.id"
                :discipline-id="occurrence.discipline_id"
                :pending-requests="pendingTeamRequests"
                :current-classification-id="occurrence.classification_id"
                @team-requested="onTeamRequested"
                @cancel-request="askCancelTeamRequest"
                @open-pendency="openPendency"
                @classification-changed="onClassificationChanged"
              />
            </div>
            <!-- /ABA: Decisões Técnicas -->
          </div>
          <!-- /tab-content-area -->
        </div>
      </div>
      <!-- /tab-layout-card -->

      <!-- ── Banner: fora da etapa de regulação ─────────────────────── -->
      <div
        v-if="occurrence.current_step !== 'regulacao'"
        class="step-forwarded-banner mt-3"
      >
        <feather-icon
          icon="CheckCircleIcon"
          size="16"
          class="mr-50 flex-shrink-0"
        />
        <div>
          <strong
            >Encaminhada para
            {{ stepLabelText(occurrence.current_step) }}</strong
          >
          <span
            class="d-block"
            style="font-size: 12px; opacity: 0.85; margin-top: 2px"
          >
            Você ainda pode registrar decisões técnicas, interrogatórios e
            informações dos envolvidos normalmente.
          </span>
        </div>
      </div>

      <occurrence-actions-bar
        :can-abandon="podeAbandonar"
        :can-finalize="podeFinalizar"
        @abandon="abandonOccurrence"
        @finalize="showFinalizeModal = true"
      />
    </div>
    <!-- /v-else (occurrence loaded) -->

    <!-- ── Modal: Adicionar Envolvido (natureza → papel → form/reuso) ── -->
    <occurrence-involved-add-modal
      v-if="occurrence"
      :visible.sync="showAddInvolvedModal"
      :occurrence-id="occurrence.id"
      :discipline-id="occurrence.discipline_id"
      @added="loadInvolved"
    />

    <!-- ── Modal: Interrogatório ──────────────────────────────────── -->
    <InterrogatoryModal
      v-if="interrModalInterrogatory"
      :interrogatory="interrModalInterrogatory"
      :visible.sync="showInterrogatoryModal"
      :review-answers="interrModalReviewAnswers"
      :mandatory="false"
      @confirm="onInterrConfirm"
      @skip="showInterrogatoryModal = false"
      @close="showInterrogatoryModal = false"
    />

    <!-- ── Modal: Cancelar solicitação de equipe ──────────────────── -->
    <b-modal
      v-model="showCancelRequestModal"
      title="Cancelar solicitação de equipe"
      ok-title="Cancelar solicitação"
      cancel-title="Voltar"
      ok-variant="danger"
      :ok-disabled="savingCancelRequest"
      @ok="confirmCancelTeamRequest"
    >
      <p v-if="cancellingRequest" class="mb-1">
        {{ cancellingRequest.quantity }}x
        {{ cancellingRequest.team_type ? cancellingRequest.team_type.name : "—" }}
      </p>
      <div class="form-group mb-0">
        <label><span class="required">*</span> Motivo:</label>
        <textarea
          v-model="cancelRequestNotes"
          class="form-control"
          rows="3"
          maxlength="1000"
          placeholder="Ex: quadro estabilizado, equipe não é mais necessária"
        />
        <small class="text-muted">Mínimo de 5 caracteres. Fica registrado na auditoria.</small>
      </div>
    </b-modal>

    <FinalizeOccurrenceModal
      :visible.sync="showFinalizeModal"
      :occurrence-id="occurrence ? occurrence.id : null"
      :discipline-id="occurrence && occurrence.discipline_id ? occurrence.discipline_id : null"
      @finalized="onFinalized"
      @open-pendency="openPendency"
    />

    <BusinessRulePendenciesModal
      :payload="rulePendencies"
      :retrying="ruleRetrying"
      title="Não é possível cancelar a solicitação"
      @retry="executeGuarded"
      @close="closeRulePendencies"
      @open="openPendency"
    />

  </div>
</template>

<script>
import ApiErrorMixin from "@/mixins/ApiErrorMixin";
import BusinessRuleGuardMixin from "@/mixins/BusinessRuleGuardMixin";
import BusinessRulePendenciesModal from "@/components/BusinessRulePendenciesModal.vue";
import { BSpinner } from "bootstrap-vue";
import InterrogatoryModal from "@/components/InterrogatoryModal.vue";
import OccurrenceTimeline from "@/components/OccurrenceTimeline.vue";
import OccurrenceMessages from "@/components/OccurrenceMessages.vue";
import InvolvedClinicalCard from "@/components/involved/InvolvedClinicalCard.vue";
import OccurrenceInvolvedAddModal from "@/components/involved/OccurrenceInvolvedAddModal.vue";
import TechnicalDecisionPanel from "@/components/TechnicalDecisionPanel.vue";
import TeamRequestsMixin from "@/mixins/TeamRequestsMixin";
import OccurrenceInfoBlock from "@/components/OccurrenceInfoBlock.vue";
import FinalizeOccurrenceModal from "@/components/FinalizeOccurrenceModal.vue";
import OccurrenceActionsBar from "@/components/OccurrenceActionsBar.vue";
import ElapsedTimeMixin from "@/mixins/ElapsedTimeMixin.js";

export default {
  mixins: [ElapsedTimeMixin, ApiErrorMixin, TeamRequestsMixin, BusinessRuleGuardMixin],
  name: "RegulacaoDetail",

  components: {
    BSpinner,
    InterrogatoryModal,
    OccurrenceTimeline,
    OccurrenceMessages,
    InvolvedClinicalCard,
    OccurrenceInvolvedAddModal,
    TechnicalDecisionPanel,
    OccurrenceInfoBlock,
    FinalizeOccurrenceModal,
    BusinessRulePendenciesModal,
    OccurrenceActionsBar,
  },

  computed: {
    podeAbandonar() {
      return !!(
        this.occurrence &&
        this.occurrence.active_responsibility &&
        this.occurrence.active_responsibility.user_id === this.$store.state.user.id
      );
    },

    podeFinalizar() {
      return !!(
        this.occurrence &&
        Number(this.occurrence.status) === 1 &&
        this.$hasAbility("ocorrencia.finalizar")
      );
    },

    pipelineSteps() {
      if (!this.occurrence) return [];

      const hasDispatches = !!(
        this.occurrence.dispatches && this.occurrence.dispatches.length > 0
      );
      const stepToPos = {
        solicita: 0,
        triagem: 0,
        regulacao: 1,
        despacha: 2,
        liberado: 3,
        em_atendimento: 4,
        encerrado: 5,
      };
      const currentPos = stepToPos[this.occurrence.current_step] ?? 0;
      const despachaActive = currentPos === 2;
      // ocorrência voltou de despacha (ex: reversão manual) mas despachos existem
      const despachaDone = hasDispatches && currentPos < 2;

      const steps = [
        { key: "solicita", label: "Solicitação", icon: "FileTextIcon", pos: 0 },
        {
          key: "regulacao",
          label: "Regulação",
          icon: "ActivityIcon",
          pos: 1,
        },
        {
          key: "despacha",
          label: despachaActive
            ? hasDispatches
              ? "Despachado"
              : "Aguardando Despacho"
            : despachaDone || currentPos > 2
            ? "Despachado"
            : "Despacho",
          icon: despachaActive && !hasDispatches ? "ClockIcon" : "TruckIcon",
          pos: 2,
          variant: despachaActive ? (hasDispatches ? "blue" : "amber") : null,
        },
        {
          key: "liberado",
          label: "Liberado",
          icon: "UnlockIcon",
          pos: 3,
        },
        {
          key: "finalizado",
          label: "Finalizado",
          icon: "CheckCircleIcon",
          pos: 4,
        },
      ];

      return steps.map((step) => ({
        ...step,
        state:
          step.pos < currentPos
            ? "done"
            : step.pos === currentPos
            ? "active"
            : step.key === "despacha" && despachaDone
            ? "done"
            : "pending",
      }));
    },

    classificationColor() {
      if (this.occurrence?.classification?.hex_color) {
        return this.occurrence.classification.hex_color;
      }
      return "#7367f0";
    },

    elapsedKpiBadge() {
      if (!this.occurrence) return "badge-light-success";
      const cls = this.elapsedBadgeClass(
        this.occurrence.current_step_since || this.occurrence.created_at,
        this.occurrence.current_step,
        this.occurrence.discipline_id,
      );
      if (cls.includes("--danger")) return "badge-light-danger";
      if (cls.includes("--warning")) return "badge-light-warning";
      return "badge-light-success";
    },
  },

  filters: {
    formatDate(val) {
      if (!val) return "—";
      return new Date(val).toLocaleString("pt-BR");
    },
  },

  data() {
    return {
      loadingOccurrence: true,
      occurrence: null,

      showFinalizeModal: false,

      showCancelRequestModal: false,
      cancellingRequest: null,
      cancelRequestNotes: "",
      savingCancelRequest: false,

      activeTab: "resumo",
      tabs: [
        { key: "resumo", label: "Resumo", icon: "FileTextIcon" },
        { key: "envolvidos", label: "Envolvidos", icon: "UsersIcon" },
        {
          key: "interrogatorios",
          label: "Interrogatórios",
          icon: "ClipboardIcon",
        },
        { key: "decisoes", label: "Decisões Técnicas", icon: "ZapIcon" },
      ],

      loadingInvolved: false,
      involvedItems: [],

      showAddInvolvedModal: false,
      involvedForm: {
        unidentified: false,
        name: "",
        apparent_description: "",
        age: null,
        age_unit: "anos",
        gender: "",
        social_name: "",
        cpf: "",
        rg: "",
        cns: "",
        birth_date: "",
        race: "",
        telephone: "",
        cellphone: "",
      },
      involvedFormErrors: {},
      addingInvolved: false,

      loadingInterrogatories: false,
      occurrenceInterrogatories: [],
      pendingInterrogatory: null,

      showInterrogatoryModal: false,
      interrModalInterrogatory: null,
      interrModalReviewAnswers: [],

      focusInvolvedId: null,
      focusPanelKey: null,
    };
  },

  async mounted() {
    await this.loadOccurrence();
    this.assumeOccurrence();
    this.watchTeamRequests();

    const { involved, panel } = this.$route.query;
    if (involved && panel) {
      this.focusPendency(Number(involved), String(panel));
    }
  },

  methods: {
    openPendency(violation) {
      this.showFinalizeModal = false;

      this.focusPendency(violation.occurrence_involved_id, violation.target);
    },

    focusPendency(occurrenceInvolvedId, panelKey) {
      if (!occurrenceInvolvedId || !panelKey) return;

      this.activeTab = "envolvidos";
      this.focusInvolvedId = Number(occurrenceInvolvedId);
      this.focusPanelKey = panelKey;

      this.$nextTick(() => {
        const el = document.getElementById(`involved-card-${occurrenceInvolvedId}`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    },

    onFinalized() {
      this.showFinalizeModal = false;
      this.$message("Sucesso", "Ocorrência finalizada.", "success");
      this.$router.push({ name: "regulacao" });
    },

    // ── Solicitações de equipe ───────────────────────────────────────

    askCancelTeamRequest(req) {
      this.cancellingRequest = req;
      this.cancelRequestNotes = "";
      this.showCancelRequestModal = true;
    },

    async confirmCancelTeamRequest(bvModalEvent) {
      bvModalEvent.preventDefault();
      const notes = this.cancelRequestNotes.trim();

      if (notes.length < 5) {
        this.$message("Atenção", "Descreva o motivo do cancelamento.", "warning");
        return;
      }

      this.savingCancelRequest = true;
      try {
        await this.runGuarded(
          options => this.$http.post(
            `${this.$store.state.api}occurrence_team_requests/${this.cancellingRequest.id}/cancel`,
            { cancel_notes: notes, ...options },
          ),
          async () => {
            this.showCancelRequestModal = false;
            this.cancellingRequest = null;
            await this.loadTeamRequests(this.occurrence.id);
            await this.refreshOccurrenceStep();
            if (this.$refs.decisionPanel) this.$refs.decisionPanel.fetchLogs();
            this.$message("Regulação", "Solicitação cancelada.", "success");
          },
        );
      } catch (e) {
        this.$message("Erro", this.apiErrorText(e, "Erro ao cancelar a solicitação."), "error");
      } finally {
        this.savingCancelRequest = false;
      }
    },

    async assumeOccurrence() {
      if (!this.occurrence) return;
      try {
        const res = await this.$http.post(
          this.$store.state.api + `occurrences/${this.occurrence.id}/assume`,
        );
        // res.data.data é o registro OccurrenceStepResponsibility com user embutido
        this.$set(this.occurrence, "active_responsibility", res.data.data);
      } catch (_) {}
    },

    async abandonOccurrence() {
      try {
        await this.$http.post(
          this.$store.state.api + `occurrences/${this.occurrence.id}/abandon`,
        );
        this.$router.push({ name: "regulacao" });
      } catch (e) {
        this.$message("Erro", this.apiErrorText(e, "Erro ao abandonar ocorrência."), "error");
      }
    },

    async loadOccurrence() {
      this.loadingOccurrence = true;
      try {
        const id = this.$route.params.id;
        const res = await this.$http.get(
          this.$store.state.api + `occurrences/${id}`,
          { params: { with: ['dispatches'] } },
        );
        this.occurrence = res.data.data || res.data;
        await Promise.all([
          this.loadInvolved(),
          this.loadInterrogatories(),
          this.loadTeamRequests(this.occurrence.id),
        ]);
      } catch (e) {
        this.$message("Erro", "Erro ao carregar ocorrência.", "error");
      } finally {
        this.loadingOccurrence = false;
      }
    },

    /**
     * A "Solicitação de equipe" agora encaminha a ocorrência automaticamente
     * (regulacao → despacha) no backend. Aqui só refletimos isso na tela —
     * atualização parcial do `current_step`, sem recarregar a página inteira.
     */
    onTeamRequested() {
      this.loadTeamRequests(this.occurrence.id);
      this.refreshOccurrenceStep();
    },

    onClassificationChanged(updatedOccurrence) {
      if (!this.occurrence || !updatedOccurrence) return;
      this.occurrence.classification_id = updatedOccurrence.classification_id;
      this.occurrence.classification = updatedOccurrence.classification;
    },

    async refreshOccurrenceStep() {
      try {
        const id = this.$route.params.id;
        const res = await this.$http.get(this.$store.state.api + `occurrences/${id}`);
        const updated = res.data.data || res.data;
        if (this.occurrence) {
          this.occurrence.current_step = updated.current_step;
          this.occurrence.current_step_since = updated.current_step_since;
          this.occurrence.status = updated.status;
        }
      } catch (e) {
        // atualização em segundo plano: falha aqui não deve interromper o operador
      }
    },

    async loadInvolved() {
      this.loadingInvolved = true;
      try {
        const res = await this.$http.get(
          this.$store.state.api + "occurrence_involveds",
          { params: { occurrence_id: this.occurrence.id } },
        );
        this.involvedItems = res.data.data || res.data;
      } catch (e) {
        this.$message("Erro", "Erro ao carregar envolvidos.", "error");
      } finally {
        this.loadingInvolved = false;
      }
    },

    async loadInterrogatories() {
      this.loadingInterrogatories = true;
      try {
        const res = await this.$http.get(
          this.$store.state.api + "occurrence_interrogatories",
          { params: { occurrence_id: this.occurrence.id } },
        );
        this.occurrenceInterrogatories = res.data.data || res.data;

        const dt = this.occurrence.dispatch_type;
        if (dt && dt.interrogatory_id) {
          const answered = this.occurrenceInterrogatories.some(
            (oi) => oi.interrogatory_id === dt.interrogatory_id,
          );
          if (!answered) {
            const interrRes = await this.$http.get(
              this.$store.state.api + `interrogatories/${dt.interrogatory_id}`,
            );
            this.pendingInterrogatory = interrRes.data.data || interrRes.data;
          }
        }
      } catch (e) {
        this.$message("Erro", "Erro ao carregar interrogatórios.", "error");
      } finally {
        this.loadingInterrogatories = false;
      }
    },

    async addInvolved() {
      this.involvedFormErrors = {};
      const isUnidentified = this.involvedForm.unidentified;
      const nameField = isUnidentified
        ? this.involvedForm.apparent_description
        : this.involvedForm.name;
      if (!nameField || !nameField.trim()) {
        this.involvedFormErrors.name = true;
        return;
      }
      this.addingInvolved = true;
      try {
        const invRes = await this.$http.post(
          this.$store.state.api + "involveds",
          { ...this.involvedForm },
        );
        const involved = invRes.data.data || invRes.data;
        await this.$http.post(this.$store.state.api + "occurrence_involveds", {
          occurrence_id: this.occurrence.id,
          involved_id: involved.id,
        });
        this.involvedForm = {
          unidentified: false,
          name: "",
          apparent_description: "",
          age: null,
          age_unit: "anos",
          gender: "",
          social_name: "",
          cpf: "",
          rg: "",
          cns: "",
          birth_date: "",
          race: "",
          telephone: "",
          cellphone: "",
        };
        this.showAddInvolvedModal = false;
        await this.loadInvolved();
        this.$message("Sucesso", "Envolvido adicionado.", "success");
      } catch (e) {
        this.$message("Erro", "Erro ao adicionar envolvido.", "error");
      } finally {
        this.addingInvolved = false;
      }
    },

    getInitials(name) {
      if (!name) return "?";
      return name
        .split(" ")
        .slice(0, 2)
        .map((w) => w[0])
        .join("")
        .toUpperCase();
    },

    openInterrFlow() {
      if (!this.pendingInterrogatory) return;
      this.interrModalInterrogatory = this.pendingInterrogatory;
      this.interrModalReviewAnswers = [];
      this.showInterrogatoryModal = true;
    },

    reviewInterrogatory(oi) {
      if (!oi.interrogatory) return;
      this.interrModalInterrogatory = oi.interrogatory;
      this.interrModalReviewAnswers = (oi.answers || []).map((a) => ({
        interrogatory_question_id: a.interrogatory_question_id,
        answer_boolean: a.answer_boolean,
        answer_text: a.answer_text,
        answer_number: a.answer_number,
        answer_alternative_id: a.answer_alternative_id,
      }));
      this.showInterrogatoryModal = true;
    },

    async onInterrConfirm(payload) {
      this.showInterrogatoryModal = false;
      try {
        await this.$http.post(
          this.$store.state.api + "occurrence_interrogatories",
          {
            occurrence_id: this.occurrence.id,
            interrogatory_id: this.pendingInterrogatory
              ? this.pendingInterrogatory.id
              : this.interrModalInterrogatory.id,
            answers: payload.answers,
          },
        );
        this.pendingInterrogatory = null;
        const res = await this.$http.get(
          this.$store.state.api + `occurrences/${this.occurrence.id}`,
        );
        this.occurrence = res.data.data || res.data;
        await this.loadInterrogatories();
        this.$message("Sucesso", "Interrogatório salvo.", "success");
      } catch (e) {
        this.$message("Erro", "Erro ao salvar interrogatório.", "error");
      }
    },

    onOutcomeSaved(updatedInvolved) {
      const idx = this.involvedItems.findIndex(
        (i) => i.id === updatedInvolved.id,
      );
      if (idx !== -1) {
        this.$set(this.involvedItems, idx, updatedInvolved);
      }
    },

    stepLabelText(step) {
      const labels = {
        solicita: "Solicitação",
        triagem: "Triagem",
        regulacao: "Regulação",
        despacha: "Despacho",
        em_atendimento: "Em Atendimento",
        encerrado: "Encerrado",
      };
      if (labels[step]) return labels[step];
      if (!step) return "—";
      return step.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    },

  },
};
</script>

<style scoped>
/* ── Occurrence header ────────────────────────────────────────── */
.occ-avatar {
  width: 104px !important;
  height: 104px !important;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.occ-header__tags {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.occ-tag {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}

.occ-tag--discipline,
.occ-tag--type,
.occ-tag--reason {
  background: #f3f2f7;
  color: #5e5873;
}

.occ-classification-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 11px;
  border: 1.5px solid;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  margin-top: 10px;
}

/* ── Equipes sugeridas ───────────────────────────────────────── */
.occ-teams-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  padding: 8px 14px;
  background: #fff8e6;
  border: 1.5px solid #ffd166;
  border-radius: 8px;
}

.occ-teams-icon {
  color: #e5a200;
  flex-shrink: 0;
}

.occ-teams-label {
  font-size: 11px;
  font-weight: 700;
  color: #c48800;
  white-space: nowrap;
  margin-right: 4px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.occ-team-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 11px;
  background: #f3f2f7;
  color: #5e5873;
  border: 1px solid #e0dfe8;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  letter-spacing: 0.2px;
}

.occ-map-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  font-size: 12px;
  background: #f3f2f7;
  border: 1px solid #e0dfe8;
  border-radius: 20px;
  color: #7367f0;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s;
  font-weight: 600;
}

.occ-map-btn:hover {
  background: #ede9fd;
  border-color: #7367f0;
}

/* Timer do card de protocolo */
.occ-timer-value {
  font-size: 20px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  letter-spacing: 1px;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 4px;
}

/* Animação piscar */
@keyframes piscar {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.35;
  }
}

.piscar {
  animation: piscar 1.5s ease-in-out infinite;
}

/* ── Card container ───────────────────────────────────────────── */
.tab-layout-card {
  overflow: hidden;
}

/* ── Sidebar ──────────────────────────────────────────────────── */
.tab-sidebar {
  display: flex;
  flex-direction: column;
  width: 110px;
  flex-shrink: 0;
  border-right: 1px solid #ebe9f1;
  padding: 8px 0;
}

.tab-sidebar-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 16px 10px;
  width: 100%;
  background: transparent;
  border: none;
  border-left: 3px solid transparent;
  cursor: pointer;
  color: #b9b9c3;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
  line-height: 1;
  outline: none;
}

.tab-sidebar-btn:hover {
  background: #f8f7ff;
  color: #7367f0;
}

.tab-sidebar-btn.active {
  background: #f3f2f7;
  border-left-color: #7367f0;
  color: #7367f0;
  font-weight: 600;
}

.tab-sidebar-label {
  font-size: 11px;
  line-height: 1.3;
  text-align: center;
  white-space: nowrap;
}

/* ── Content area ─────────────────────────────────────────────── */
.tab-content-area {
  flex: 1;
  min-width: 0;
  padding: 18px 20px;
}

/* ── Interrogatórios ──────────────────────────────────────────── */
.interr-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
  color: #b9b9c3;
  gap: 8px;
  font-size: 13px;
}

.interr-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.interr-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border-radius: 8px;
  border: 1px solid #ebe9f1;
  background: #fff;
}

.interr-item--pending {
  border-left: 4px solid #ff9f43;
  background: #fffaf4;
}

.interr-item--done {
  border-left: 4px solid #28c76f;
  background: #f6fef9;
}

.interr-item__icon {
  flex-shrink: 0;
}

.interr-item--pending .interr-item__icon {
  color: #ff9f43;
}
.interr-item--done .interr-item__icon {
  color: #28c76f;
}

.interr-item__body {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.interr-item__name {
  font-weight: 600;
  font-size: 14px;
  color: #5e5873;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.interr-item__badge {
  display: inline-block;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  width: fit-content;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.interr-item__badge--warning {
  background: #fff3e0;
  color: #ff9f43;
}

.interr-item__badge--success {
  background: #e8faf0;
  color: #28c76f;
}

.interr-item__date {
  font-size: 11px;
  color: #b9b9c3;
}

/* ── Accordion de envolvidos ──────────────────────────────────── */
.involved-card {
  border: 1px solid #ebe9f1;
  border-radius: 8px;
  overflow: hidden;
}

.involved-card__header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  cursor: pointer;
  background: #f8f7ff;
  border-left: 4px solid #7367f0;
  transition: background 0.15s;
  user-select: none;
}

.involved-card__header:hover {
  background: #f0effe;
}

.involved-card__avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #7367f0;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  flex-shrink: 0;
}

.involved-card__info {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.involved-card__name {
  font-weight: 600;
  font-size: 14px;
  color: #5e5873;
}

.involved-card__meta {
  font-size: 11px;
  color: #b9b9c3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.involved-card__body {
  padding: 16px;
  border-top: 1px solid #ebe9f1;
}

/* ── Histórico de evoluções ───────────────────────────────────── */
.vs-history {
  border: 1px solid #ebe9f1;
  border-radius: 8px;
  overflow: hidden;
}

.vs-history__header {
  display: flex;
  align-items: center;
  padding: 8px 14px;
  background: #f8f7ff;
  font-size: 12px;
  font-weight: 600;
  color: #7367f0;
  border-bottom: 1px solid #ebe9f1;
}

.vs-history__table thead th {
  font-size: 11px;
  font-weight: 600;
  color: #b9b9c3;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  padding: 6px 10px;
  border-top: none;
  background: #fafafa;
}

.vs-history__table tbody td {
  font-size: 12px;
  padding: 6px 10px;
  color: #5e5873;
  vertical-align: middle;
}

.vs-obs-cell {
  max-width: 160px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.vs-form-title {
  font-size: 13px;
  font-weight: 600;
  color: #5e5873;
}

/* ── Dark theme ───────────────────────────────────────────────── */
.dark-layout .tab-sidebar {
  border-right-color: #3b4253;
}

.dark-layout .tab-sidebar-btn {
  color: #676d7d;
}

.dark-layout .tab-sidebar-btn:hover {
  background: #161d31;
  color: #7367f0;
}

.dark-layout .tab-sidebar-btn.active {
  background: #161d31;
  border-left-color: #7367f0;
  color: #7367f0;
}

/* ── Resumo tab ───────────────────────────────────────────── */
.resumo-strip {
  display: flex;
  align-items: center;
  background: #fff;
  border: 1px solid #ebe9f1;
  border-radius: 8px;
  padding: 14px 20px;
  flex-wrap: wrap;
  gap: 12px;
}

.resumo-strip-card {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 150px;
}

.resumo-strip-divider {
  width: 1px;
  height: 36px;
  background: #ebe9f1;
  flex-shrink: 0;
}

.resumo-strip-label {
  display: block;
  font-size: 11px;
  color: #b9b9c3;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  font-weight: 600;
  line-height: 1;
  margin-bottom: 3px;
}

.resumo-strip-value {
  font-size: 13px;
  color: #5e5873;
  line-height: 1.2;
}

.resumo-queixa {
  background: #f8f7ff;
  border: 1px solid #ede9fe;
  border-left: 4px solid #7367f0;
  border-radius: 8px;
  padding: 14px 16px;
}

.resumo-queixa__head {
  display: flex;
  align-items: center;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #7367f0;
  margin-bottom: 8px;
}

.resumo-queixa__body {
  font-size: 14px;
  color: #5e5873;
  line-height: 1.6;
}

.resumo-dt {
  border-collapse: collapse;
}

.resumo-dt__label {
  font-size: 11px;
  font-weight: 700;
  color: #b9b9c3;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  padding: 11px 16px;
  white-space: nowrap;
  width: 150px;
  border-bottom: 1px solid #f8f7ff;
  vertical-align: middle;
}

.resumo-dt__value {
  font-size: 13px;
  color: #5e5873;
  padding: 11px 16px 11px 0;
  border-bottom: 1px solid #f8f7ff;
  vertical-align: middle;
}

tr:last-child .resumo-dt__label,
tr:last-child .resumo-dt__value {
  border-bottom: none;
}

.resumo-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
}

.resumo-badge--success {
  background: #e8faf0;
  color: #28c76f;
}

.resumo-badge--warning {
  background: #fff3e0;
  color: #ff9f43;
}

.resumo-tl-section {
  margin-top: 4px;
}

.step-forwarded-banner {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 12px 16px;
  background: #e8f5e9;
  border: 1px solid #a5d6a7;
  border-left: 4px solid #28c76f;
  border-radius: 8px;
  color: #1b5e20;
  font-size: 13px;
}

.dark-layout .step-forwarded-banner {
  background: rgba(40, 199, 111, 0.1);
  border-color: rgba(40, 199, 111, 0.3);
  border-left-color: #28c76f;
  color: #28c76f;
}

.resumo-tl-title {
  display: flex;
  align-items: center;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #5e5873;
}

/* dark */
.dark-layout .resumo-strip {
  background: #283046;
  border-color: #3b4253;
}

.dark-layout .resumo-strip-divider {
  background: #3b4253;
}

.dark-layout .resumo-strip-value {
  color: #d0d2d6;
}

.dark-layout .resumo-queixa {
  background: #1c2333;
  border-color: #3b4253;
}

.dark-layout .resumo-queixa__body {
  color: #d0d2d6;
}

.dark-layout .resumo-dt__label,
.dark-layout .resumo-dt__value {
  border-bottom-color: #283046;
}

.dark-layout .resumo-dt__value {
  color: #d0d2d6;
}

/* ── Pipeline horizontal ─────────────────────────────────────── */
.occ-htl {
  background: #fff;
  border: 1px solid #ebe9f1;
  border-radius: 10px;
  padding: 16px 24px;
  overflow-x: auto;
}

.occ-htl-track {
  display: flex;
  align-items: center;
  min-width: 420px;
}

/* Conectores */
.occ-htl-connector {
  flex: 1;
  height: 2px;
  background: #e0dfe8;
  min-width: 12px;
  flex-shrink: 1;
}

.occ-htl-connector--done {
  background: #28c76f;
}

/* Nós */
.occ-htl-node {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 7px;
  flex-shrink: 0;
}

/* Dots */
.occ-htl-dot {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 2px solid #d8d6de;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.occ-htl-dot--done {
  background: #28c76f;
  border-color: #28c76f;
}

.occ-htl-dot--active-primary {
  background: #7367f0;
  border-color: #7367f0;
  animation: htl-pulse-primary 2s ease-in-out infinite;
}
@keyframes htl-pulse-primary {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(115, 103, 240, 0.3);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(115, 103, 240, 0);
  }
}

.occ-htl-dot--active-amber {
  background: #f59e0b;
  border-color: #f59e0b;
  animation: htl-pulse-amber 2s ease-in-out infinite;
}
@keyframes htl-pulse-amber {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.3);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(245, 158, 11, 0);
  }
}

.occ-htl-dot--active-blue {
  background: #2563eb;
  border-color: #2563eb;
  animation: htl-pulse-blue 2s ease-in-out infinite;
}
@keyframes htl-pulse-blue {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(37, 99, 235, 0.3);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(37, 99, 235, 0);
  }
}

/* Labels */
.occ-htl-label {
  font-size: 10px;
  font-weight: 600;
  color: #b9b9c3;
  text-align: center;
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  line-height: 1.3;
  max-width: 80px;
}

.occ-htl-label--done {
  color: #28c76f;
}
.occ-htl-label--active-primary {
  color: #7367f0;
}
.occ-htl-label--active-amber {
  color: #d97706;
}
.occ-htl-label--active-blue {
  color: #2563eb;
}

/* dark */
.dark-layout .occ-htl {
  background: #283046;
  border-color: #3b4253;
}

.dark-layout .occ-htl-connector {
  background: #3b4253;
}

.dark-layout .occ-htl-dot {
  background: #283046;
  border-color: #3b4253;
}
</style>
